import {makeStyles} from '@material-ui/core';
import {Colors} from '../../../styles/Colors';
import dayjs from '../../../services/customDayJs';
import moment from 'moment';
import {getDurationFromSecShortestFormat} from '../../../utils/transformers';
import i18n from '../../../services/i18n';
import {TFunction} from 'react-i18next';
import {CallActivityMetric, CallActivityMetrics, MetricFilter} from '../../../store/actions/calls/payloads';
import {convertFormatFromBackendToDayJs, convertUtcToUserLocalTime} from '../../../utils/dateWithTimezoneConversion';

export type CallStatisticsFormProps = {
    metric: 'connected_calls' | 'talk_time' | 'hold_time' | 'wrapup_time';
    ringGroup: string;
    agent: number | null;
    compareWithPrevious: boolean;
    period: number;
    fromDate?: string | null;
    toDate?: string | null;
    
    fromDateFilter?: string | null;
    toDateFilter?: string | null;
    
    fromDateCompareFilter?: string | null;
    toDateCompareFilter?: string | null;

    unit: "week" | "day" | "hour" | "min";
    unitValue: number;

    dateFromFormated?: string | null;
    dateToFormated?: string | null;
};

export type CallStatisticsMetricsItem = {
    metric: 'connected_calls' | 'talk_time' | 'hold_time' | 'wrapup_time';
    name: string,
    date: Date,
    incoming: number,
    outgoing: number,
    incoming_compare?: number,
    outgoing_compare?: number,
    date_compare?: Date,
    name_compare: string,
    metric_date?: string;
    metric_date_compare?: string;
};

export type MetricUnit = {
    value: number;
    unit: "week" | "day" | "hour" | "min";
    label: string;
    disabled: boolean;
};

export type MetricsCsvItem = {
    column1: string;
    column2: string;
    column3: string;
    column4: string;
}

const dateFormatDefault = 'YYYY-MM-DD HH:mm:ss';

export const initialValues: CallStatisticsFormProps = {
    metric: 'talk_time',
    ringGroup: '',
    agent: null,
    compareWithPrevious: false,
    period: 0,
    fromDateFilter: dayjs.utc().startOf('day').format(dateFormatDefault),
    toDateFilter: dayjs.utc().format(dateFormatDefault),
    unit: "hour",
    unitValue: 1
};

export const useStyles = makeStyles(() => ({
    disclaimerContainer: {
        display: "flex",
        justifyContent:'center'
    },
    loadDataInProgress: {
        opacity: .5
    },
    spinnerContainer: {
        position: 'absolute',
        display: 'flex',
        height: '100%',
        width: '100%',
        alignItems: 'center',
        justifyContent: 'center'
    },
    formContainer: {
        minWidth: 1124.910,
        userSelect: 'none',
    },
    filtersContainer: {
        display: 'flex',
        flexDirection: 'column',
    },
    itemsContainer: {
        marginTop: 10,
        display: 'flex',
        alignItems: 'flex-start',
    },
    inputsContainer: {
        display: 'flex',
        alignItems: 'flex-start',

        '& .MuiFormControl-root': {
            marginRight: 18,
        },

        '& .date-time-picker': {
            '& .MuiInputBase-input': {
                width: 208,
            },
        },
    },
    inputs: {
        '& .MuiFormControl-root:first-of-type': {
            marginRight: 90,
        },
    },
    button: {
        '& .MuiButton-label': {
            color: Colors.White,
        },
        minWidth: 96,
        width: 'fit-content',
        marginTop: 8,
        marginLeft: 16,
    },
    ringGroupField: {
        width: '200px !important',
        height: '56px !important',
        marginRight: 16,
    },
    timeRangeField: {
        width: '200px !important',
        height: '56px !important',
        marginRight: 16,
    },
    dropDownMultiSelect: {
        minHeight: 53,
        height: 53,
        padding: 0,
        width: 200,
        maxWidth: 200,

        '& .MuiAutocomplete-inputRoot': {
            height: 'fit-content !important',
            minHeight: 53,
            padding: 0
        }
    },
    downloadButton: {
        backgroundColor: Colors.Secondary5,
        '& .MuiButton-label': {
            color: Colors.Secondary1,
        },

        '& svg > path': {
            fill: Colors.Secondary1,
        },
        '&:hover': {
            backgroundColor: Colors.Secondary1,
            '& .MuiButton-label': {
                color: Colors.White,
            },
            '& svg > path': {
                fill: Colors.White,
            },
        },
        fontWeight: 700,
        borderRadius: '4px',
        padding: '6px 12px',
        textTransform: 'none',
        fontSize: 16,
        minWidth: 96,
        marginTop: 8,
    },
    dataContainer: {
        position: 'relative',
        width: 'fit-content',
        minWidth: 776,
        padding: 24,
        display: 'flex',
        flexDirection: 'column',
        height: 'fit-content',
        background: Colors.White,
        border: '1px solid ' + Colors.Border,
        marginTop: 24,
        borderRadius: 4,
    },
    dataContainerRow: {
        display: 'flex',
        flexDirection: 'row',
        width: '100%',
        height: 'fit-content',
        justifyContent: 'space-between'
    },
    dataContainerRowUnderlined: {
        borderBottom: '1px solid ' + Colors.Border
    },
    supportColor: {
        color: Colors.Support + ' !important'
    },
    errorColor: {
        color: Colors.Error + ' !important'
    },
    blackColor: {
        color: Colors.Black + ' !important'
    },
    extensionField: {
        width: 'fit-content',
        minWidth: 220,
    },
    compareWithPrevious: {
        width: 'fit-content',
        height: 'fit-content',
        marginBottom: 0,
        marginTop: 24,
    },
    compareWithPreviousLabel: {
        fontWeight: 400,
        lineHeight: '22px'
    },
    hiddenFocusInput: {
        width: 0,
        height: 0,
        border: 'none',
        color: 'transparent',
        position: 'absolute',
        top: -1000
    },
    datesContainer: {
        width: '100%',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginBottom: 16,
    },
    datesContainerText: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'flex-start',
    },
    dateValue: {
        color: Colors.Gray6,
    },
    vs: {
        fontWeight: 500,
        fontSize: 18,
        marginLeft: 24,
        marginRight: 24,
    },
    chartContainer: {
        width: 1040,
        padding: '24px 16px 24px 16px',
        border: '1px solid rgba(221, 225, 231, 1)',

        '& .recharts-tooltip-cursor': {
            fill: 'none !important',
            stroke: 'none !important',
        }
    },

    chartHeader: {
        width: '100%',
        height: 44,
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginBottom: 24,
    },

    chartTitleAndLegendContainer: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'flex-start',
    },

    chartTitle: {
        marginRight: 41,
        fontWeight: 500,
        fontSize: 20,
        lineHeight: '28px',
        marginLeft: 16,
        marginTop: 8,
    },

    legend: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'flex-start',
        height: 'fit-content',
        width: 'fit-content',
        paddingTop: 4,
    },

    legendCallType: {
        marginRight: 24,
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'flex-start',
        cursor: 'pointer',
        padding: 8,
        '&:hover': {
            backgroundColor: Colors.Gray11
        },
        borderRadius: 16,
        userSelect: 'none'
    },

    legendIcon: {
        width: 15,
        height: 15,
        borderRadius: 4,
        marginTop: 3,
    },

    legendIconOutgoing: {
        color: 'rgba(81, 166, 237, 1)',
        background: 'rgba(81, 166, 237, 1)'
    }, 

    legendIconIncoming: {
        color: 'rgba(245, 132, 31, 1)',
        background: 'rgba(245, 132, 31, 1)'
    },

    legendIconInactive: {
        opacity: 0.5
    },

    legendText: {
        fontWeight: 400,
        fontSize: 16,
        lineHeight: '22px',
        color: 'rgba(33, 123, 199, 1)',
        marginLeft: 8,
    },
    
    legendTextInactive: {
        textDecoration: 'line-through'
    },

    unitsSelectorContainer: {
        width: 'fit-content',
        padding: 4,
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'flex-end',
    },

    unitItem: {
        width: 72,
        height: 27,
        borderRadius: 28,
        fontWeight: 400,
        fontSize: 15,
        lineHeight: '20px',
        color: 'rgba(76, 134, 183, 1)',
        cursor: 'pointer',
        textAlign: 'center',
        paddingTop: 9,
        userSelect: 'none',
        
        '&:hover': {
            backgroundColor: Colors.Gray11
        },
    },

    unitItemSelected: {
        color: Colors.White,
        backgroundColor: 'rgba(76, 134, 183, 1)',
        
        '&:hover': {
            backgroundColor: 'rgba(76, 134, 183, 0.9)',
        },
    },

    timelapContainer: {
        width: 976,
        height: 113,
        marginTop: 17,
        marginBottom: 9,
        
        '& rect.visx-brush-selection': {
            rx: '8px !important',
            ry: '8px !important',
        }
    },

    tooltipContainer: {
        backgroundColor: '#000',
        color: '#ccc',
        padding: '18px 24px 18px 24px',
        borderRadius: '4px',
        fontSize: '12px',
        whiteSpace: 'nowrap',
        pointerEvents: 'none',
        boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
        zIndex: 100,
    },

    toolTipHeader: {
        color: 'rgba(176, 176, 176, 1)',
        fontWeight: 400,
        fontSize: 13,
        lineHeight: '16px',
        display: 'flex',
        justifyContent: 'flex-start',
        flexDirection: 'row',
        marginBottom: 2,
        width: 'fit-content',
        overflow: 'hidden',
        height: 'fit-content',
        flexWrap: 'wrap',
    },

    tooltipDetailsText: {
        color: Colors.White,
        fontWeight: 400,
        fontSize: 14,
        lineHeight: '18px',
        marginTop: 2,
        display: 'flex',
        justifyContent: 'space-between',
        flexDirection: 'row'
    },

    tooltipValueContainer: {
        overflow: 'hidden',
        whiteSpace: 'nowrap', 
        width: 'fit-content',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        fontSize: 14,
        linHeight: '100%',
        textAlign: 'right',
        marginRight: 5,
    },
    
    limitContainer: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'flex-start',
        padding: '14px 20px 8px 20px',
        marginTop: 6,
        marginBottom: 19,
        userSelect: 'none',
        background: 'rgba(221, 225, 231, 0.5)',
        borderRadius: 4,
        width: 'fit-content'
    },

    limitLabel: {
        fontWeight: 400,
        fontSize: 16,
        lineHeight: '20px',
        verticalAlign: 'middle',
        color: Colors.Text3,
        marginTop: 4,
    },

    limitIcon: {
        color: Colors.Text3 + ` !important`,
        '& svg': {
            '& path': {
                fill: Colors.Text3 + ` !important`,
                fillOpacity: 1,
            },
        },
        marginRight: 12,
    },
}));

export const formatDuration = (seconds: number, defaultValue = '00:00:00') => {
    if (!seconds) {
        return defaultValue;
    }
    const duration = dayjs.duration(seconds, 'seconds');
    let format = 'HH:mm:ss';

    if (seconds >= 3600) {
        const hours = Math.trunc(seconds / 3600);
        format = (hours + '').padStart(2, '0') + ':mm:ss';
    }

    const formatted = duration.format(format);
    return formatted;
};

export const getFullDayDifference = (date1: string, date2: string, format: string) => {
    const d1 = moment(date1, format).startOf('day');
    const d2 = moment(date2, format).startOf('day');
    return Math.round(Math.abs(d1.diff(d2, 'days', true)));
}

export const addDaysToDate = (dateStr: string, daysToAdd: number, format: string) => {
    return moment(dateStr, format)
        .add(daysToAdd, 'days')
        .format(format);
}


export const extractDateOnly = (dateStr: string, parseFormat: string, userDateTimeFormat: string): string => {
    return moment(dateStr, parseFormat).format(userDateTimeFormat).split(/\s/)[0];
}

export const extractTimeOnly = (dateStr: string, parseFormat: string, userDateTimeFormat: string): string => {
    return moment(dateStr, parseFormat).format(userDateTimeFormat).split(/\s/)[1];
}

export const extractTimeNoSeconds = (dateStr: string, parseFormat: string, userDateTimeFormat: string): string => {
    const userDateFormatisEmpty = (userDateTimeFormat?.length ?? 0) == 0;
    const value = moment(dateStr, parseFormat)
        .format(!userDateFormatisEmpty 
            ? userDateTimeFormat
            : dateFormatDefault)
        .split(/\s/)[1];
    return  value.slice(0, 5);
}

export const getDurationStringWithoutSeconds = (value: number) => {
    const valueNoSeconds = value > 60
        ? value - (value % 60)
        : value;
    let str = getDurationFromSecShortestFormat(valueNoSeconds).string;
    if(value >= 60) {
        str = str.replace(" 0" + i18n.t<string>('units:timeShortest.seconds'), "")
    }
    return str;
}

export const formatSecondsToHHMMSS = (totalSeconds: number): string => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    const pad = (n: number) => n.toString().padStart(2, '0');

    return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
}

export const generateData = (call_activity_metric_list: CallActivityMetric[] | undefined, 
    call_activity_metric_list_to_compare: CallActivityMetric[] | undefined, 
    userDateTimeFormat: string, 
    call_activity_metrics_payload: CallActivityMetrics | undefined,
    timezoneOffset: number
) => {
    const defaultDateTimeFormat = 'YYYY-MM-DD HH:mm:ss';
        const array: CallStatisticsMetricsItem[] = [];
            let incomingData: CallActivityMetric | null = null;
            let outgoingData: CallActivityMetric | null = null;
            
            let incomingCompareData: CallActivityMetric | null = null;
            let outgoingCompareData: CallActivityMetric | null = null;
            if(call_activity_metric_list?.length) {
                for(let i = 0; i < call_activity_metric_list.length; i++)
                {
                    if(call_activity_metric_list[i].name.endsWith('.outgoing')) {
                        outgoingData = call_activity_metric_list[i];
                    }
                    if(call_activity_metric_list[i].name.endsWith('.incoming')) {
                        incomingData = call_activity_metric_list[i];
                    }
                }
            }
            if(call_activity_metric_list_to_compare?.length) {
                for(let i = 0; i < call_activity_metric_list_to_compare.length; i++)
                {
                    if(call_activity_metric_list_to_compare[i].name.endsWith('.outgoing')) {
                        outgoingCompareData = call_activity_metric_list_to_compare[i];
                    }
                    if(call_activity_metric_list_to_compare[i].name.endsWith('.incoming')) {
                        incomingCompareData = call_activity_metric_list_to_compare[i];
                    }
                }
            }
            if(call_activity_metric_list){
                for(let i = 0; i < call_activity_metric_list[0].metric_data.length; i++)
                {
                    const metricValue = call_activity_metric_list[0].metric_data[i];
                    const metricValueCompare = call_activity_metric_list_to_compare?.[0].metric_data[i];
                    const incomingDataValue = incomingData?.metric_data?.[i];
                    const outgoingDataValue = outgoingData?.metric_data?.[i];
                    const incomingCompareDataValue = incomingCompareData?.metric_data?.[i];
                    const outgoingCompareDataValue = outgoingCompareData?.metric_data?.[i];
                    const unit = call_activity_metrics_payload?.metric_list?.[0]?.aggregator_list?.[0].unit ?? 'min';
                    const onlyTime = unit == 'min' || unit == 'hour';
                    array.push(
                        {
                            metric: call_activity_metrics_payload?.metric ?? 'talk_time',
                            name: onlyTime 
                                ? extractTimeNoSeconds(metricValue.date, defaultDateTimeFormat, convertFormatFromBackendToDayJs(userDateTimeFormat))
                                : extractDateOnly(
                                    convertUtcToUserLocalTime(metricValue.date, timezoneOffset, defaultDateTimeFormat)
                                    , defaultDateTimeFormat, convertFormatFromBackendToDayJs(userDateTimeFormat)),
                            date: new Date(metricValue.date),
                            incoming: incomingDataValue?.value ?? 0,
                            outgoing: outgoingDataValue?.value ?? 0,
                            outgoing_compare: outgoingCompareDataValue?.value ?? 0,
                            incoming_compare: incomingCompareDataValue?.value ?? 0,
                            date_compare: outgoingCompareDataValue
                                ? new Date(outgoingCompareDataValue?.date)
                                : undefined,
                            name_compare: onlyTime 
                                ? extractTimeNoSeconds(metricValueCompare?.date ?? metricValue.date, defaultDateTimeFormat, convertFormatFromBackendToDayJs(userDateTimeFormat))
                                : extractDateOnly(
                                    convertUtcToUserLocalTime(metricValueCompare?.date ?? metricValue.date, timezoneOffset, defaultDateTimeFormat),
                                    defaultDateTimeFormat, convertFormatFromBackendToDayJs(userDateTimeFormat)),
                            metric_date: convertUtcToUserLocalTime(metricValue.date, timezoneOffset, convertFormatFromBackendToDayJs(userDateTimeFormat)),
                            metric_date_compare: convertUtcToUserLocalTime(metricValueCompare?.date ?? metricValue.date, timezoneOffset, convertFormatFromBackendToDayJs(userDateTimeFormat))
                        }
                    );
                }
            }
        return array;
    }

export const generateCsvDataOfCallStatistic = (
    t: TFunction<string>, 
    values: CallStatisticsFormProps,
    call_activity_metrics_payload: CallActivityMetrics | undefined,
    data: CallStatisticsMetricsItem[],
    incomingShown: boolean, 
    outgoingShown: boolean, 
    call_activity_metrics_payload_to_compare: CallActivityMetrics | undefined,
): MetricsCsvItem[] => {

    const metric = call_activity_metrics_payload?.metric ?? 'talk_time';
    const filters = call_activity_metrics_payload?.metric_list?.[0].filter_list ?? [];
        const aggregator = call_activity_metrics_payload?.metric_list?.[0].aggregator_list?.[0];

        let aggregatorString = '';
        switch(metric) {
            case 'connected_calls':
                aggregatorString += t('screens:calls.countPer');
                break;
            default:
                aggregatorString += t('screens:calls.sumPer');
                break;
        }

        aggregatorString += ' ';
        if(aggregator) {
            if(aggregator.value == 1) {
                aggregatorString += t(`enums:aggregatorUnit.${aggregator.unit}`);
            } else {
                aggregatorString += aggregator.value + ' ' +  t(`enums:aggregatorUnit.${aggregator.unit}`);
            }
        }

        const filterToDescription = (filter: MetricFilter) => {
            return filter.name === 'group_id' 
                ? t('screens:calls.ringGroup') + ' ' + filter.value
                : t('screens:calls.extension') + ' ' + filter.value;
        };

        const csvData: MetricsCsvItem[] = [{
            column1: t('screens:calls.fromDateTime'),
            column2: t('screens:calls.toDateTime'),
            column3: t('screens:calls.filters'),
            column4: t('screens:calls.aggregator'),
        },{
            column1: values.dateFromFormated ?? '',
            column2: values.dateToFormated ?? '',
            column3: filters.length > 0 
                ? filterToDescription(filters[0])
                : t('common:none'),
            column4: aggregatorString
        }];

        if(filters.length > 1) {
            for(let i = 1; i < filters.length; i++ ) {
                const filter = filters[i];
                csvData.push({
                    column1: '',
                    column2: '',
                    column3: filterToDescription(filter),
                    column4: ''
                });
            }
        }

        // Adding empty line
        csvData.push({
            column1: '',
            column2: '',
            column3: '',
            column4: ''
        });
        
        let outgoingHeader = '';
        let incomingHeader = '';

        switch(metric) {
            case 'connected_calls':
                outgoingHeader = t('screens:calls.connectedCallsOutgoing');
                incomingHeader = t('screens:calls.connectedCallsIncoming');
                break;
            case 'hold_time':
                outgoingHeader = t('screens:calls.holdTimeOutgoing');
                incomingHeader = t('screens:calls.holdTimeIncoming');
                break;
            case 'wrapup_time':
                outgoingHeader = t('screens:calls.wrapupTimeOutgoing');
                incomingHeader = t('screens:calls.wrapupTimeIncoming');
                break;
            default:
                outgoingHeader = t('screens:calls.talkTimeOutgoing');
                incomingHeader = t('screens:calls.talkTimeIncoming');
                break;
        }

        csvData.push({
            column1: t('screens:calls.csvDateTime'),
            column2: outgoingShown
                ? outgoingHeader
                : incomingHeader,
            column3: outgoingShown && incomingShown 
                ? incomingHeader
                : '',
            column4: ''
        });

        const showFullDate = aggregator && ((aggregator.unit == 'min' && aggregator.value == 30 && data.length > 48)
            || (aggregator.unit == 'hour' && aggregator.value == 1 && data.length > 24)
            || (aggregator.unit == 'day' && +aggregator.value >= 1)
            || (aggregator.unit == 'week' && +aggregator.value >= 1));

        if(call_activity_metrics_payload_to_compare?.date_from) {
            for(const di of data) {

                csvData.push({
                    column1: showFullDate ? (di.metric_date_compare ?? di.name_compare) : di.name_compare,
                    column2: outgoingShown 
                        ? (metric == 'connected_calls' ? (di.outgoing_compare ?? 0) + '' : formatSecondsToHHMMSS(di.outgoing_compare ?? 0))
                        : (metric == 'connected_calls' ? (di.incoming_compare ?? 0) + '' : formatSecondsToHHMMSS(di.incoming_compare ?? 0)),
                    column3: outgoingShown && incomingShown
                        ? (metric == 'connected_calls' ? (di.incoming_compare ?? 0) + '' : formatSecondsToHHMMSS(di.incoming_compare ?? 0))
                        : '',
                    column4: ''
                });
            }
        }

        for(const di of data) {
            csvData.push({                
                column1: showFullDate ? (di.metric_date ?? di.name) : di.name,
                column2: outgoingShown 
                    ? (metric == 'connected_calls' ? (di.outgoing ?? 0) + '' : formatSecondsToHHMMSS(di.outgoing))
                    : (metric == 'connected_calls' ? (di.incoming ?? 0) + '' : formatSecondsToHHMMSS(di.incoming)),
                column3: outgoingShown && incomingShown
                    ? (metric == 'connected_calls' ? (di.incoming ?? 0) + '' : formatSecondsToHHMMSS(di.incoming))
                    : '',
                column4: ''
            });
        }

        return csvData;
}

export const roundTimeTo30Seconds = (value: number) => {
    return Math.ceil(value / 30) * 30;
} 